import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  MenuItem,
  Grid,
  CircularProgress,
  Backdrop,
  Tabs,
  Tab,
  Collapse,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { RootState } from "../../store/store";
import { setCurrentEvent } from "../../reducers/event_reducer";
import { setHallsList } from "../../reducers/hall_reducer";
import events_service from "../../services/events_service";
import halls_service from "../../services/halls_service";
import EventSeatingPlanTable from "../../Components/SeatingPlanComponents/EventSeatingPlanTable";
import seating_plan_service from "../../services/seating_plan_service";
import { toast } from "react-toastify";
import ReactCountryFlag from "react-country-flag";
import { useTranslation } from "react-i18next";

const StyledRoot = styled(Box)(({ theme }) => ({
  width: "100%",
  height: "100%",
  paddingBottom: theme.spacing(2),
}));

const StyledContainer = styled(Box)({
  width: "98%",
  height: "95%",
  backgroundColor: "#fff",
  padding: "20px",
  overflowY: "auto",
});

const ButtonContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  marginBottom: theme.spacing(2),
}));

const StyledButton = styled(Button)({
  backgroundColor: "#4D67EB",
  fontSize: "12px",
  color: "#fff",
  "&:hover": {
    backgroundColor: "#3755a3",
  },
});

const ReportButton = styled(Button)({
  backgroundColor: "#4D67EB",
  fontSize: "14px",
  color: "#fff",
  padding: "10px 20px",
  minWidth: "120px",
  "&:hover": {
    backgroundColor: "#3755a3",
  },
});

const SaveButton = styled(Button)({
  backgroundColor: "#E53E3E",
  fontSize: "16px",
  color: "#fff",
  padding: "12px 30px",
  minWidth: "140px",
  fontWeight: "bold",
  boxShadow: "0 4px 8px rgba(229, 62, 62, 0.3)",
  "&:hover": {
    backgroundColor: "#C53030",
    boxShadow: "0 6px 12px rgba(229, 62, 62, 0.4)",
  },
});

interface SeatingCell {
  price: number | null;
  status: "open" | "invitation" | "closed" | "";
  ordered: "yes" | "no" | "";
  category: string;
}

interface FormDataShape {
  eventName_en: string;
  eventName_am: string;
  description_en: string;
  description_am: string;
  eventImage: File | string | null;
  dateTime: string;
  eventCategory: string;
  hallID: string;
  seatingPlanID: string;
  status: string;
  hasSeatmap: boolean;

  // Fields for halls without a seatmap
  seat_count: number | null;
  price_seat: number | null;

  // Fields for closed/invitations (top-level counters)
  closedSeatsCount: number | null;
  invitationSeatsCount: number | null;

  [key: string]: any;
}

const EventsCreationPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { hallsReducer } = useSelector((state: RootState) => state);
  const { hallsList } = hallsReducer;
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();

  // State: whether the selected hall has a seatmap
  const [selectedHallHasSeatmap, setSelectedHallHasSeatmap] = useState(false);

  // Main form data
  const [formData, setFormData] = useState<FormDataShape>({
    eventName_en: "",
    eventName_am: "",
    description_en: "",
    description_am: "",
    eventImage: null,
    dateTime: "",
    eventCategory: "",
    hallID: "",
    seatingPlanID: "",
    hasSeatmap: false,
    status: "Draft",
    seat_count: null,
    price_seat: null,
    closedSeatsCount: null,
    invitationSeatsCount: null,
  });

  // Validation errors
  const [errors, setErrors] = useState<{ [key: string]: boolean }>({
    eventName_en: false,
    eventName_am: false,
    dateTime: false,
    eventCategory: false,
    hallID: false,
  });

  // The seat map data (for seat-by-seat usage)
  const [seatingData, setSeatingData] = useState<any>(null);

  // Filtered seat plan list for the currently-selected hall
  const [filteredSeatingPlans, setFilteredSeatingPlans] = useState<any[]>([]);

  // All seat plans from backend
  const [seatingList, setSeatingList] = useState<any[]>([]);

  // Loading spinner
  const [loading, setLoading] = useState(false);

  // Preview image for event
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  // Which language tab
  const [language, setLanguage] = useState<"en" | "am">("en");

  // Collapse state for event details
  const [showEventDetails, setShowEventDetails] = useState(!id); // Show details by default for new events, hide for editing

  // Additional seat info
  const [countOfClosedSeats, setCountOfClosedSeats] = useState<any>(null);
  const [countOfInvitationSeats, setCountOfInvitationSeats] = useState<any>(
    null
  );
  const [commentOfClosedSeats, setCommentOfClosedSeats] = useState("");
  const [commentOfInvitationSeats, setCommentOfInvitationSeats] = useState("");

  /**
   * 1) Load Halls List on mount
   */
  useEffect(() => {
    halls_service.getHallsList().then((res) => {
      const fetchedHallsList = res.data.status.halls;
      if (fetchedHallsList?.length) {
        dispatch(setHallsList(fetchedHallsList));
      }
    });
  }, [dispatch]);

  /**
   * 2) Load Seat Plans on mount
   */
  useEffect(() => {
    seating_plan_service.getSeatingPlansList().then((res) => {
      setSeatingList(res.data.status?.seating || []);
    });
  }, [dispatch]);

  /**
   * Filter seat plans by hallID.
   * If seatPlanID is supplied (editing), try that plan first; otherwise, fallback to the first.
   */
  const filterSeatingPlans = (hallID: string, seatPlanID?: string) => {
    const filtered = seatingList.filter((plan) => plan.hall_id === hallID);
    setFilteredSeatingPlans(filtered);

    // If no seat plans exist for this hall
    if (filtered.length === 0) {
      setFormData((prev) => ({
        ...prev,
        seatingPlanID: "",
      }));
      return;
    }

    // If we have a seatPlanID (editing)
    if (seatPlanID) {
      const matchedPlan = filtered.find(
        (plan: any) => plan.seating_id === seatPlanID
      );
      if (matchedPlan) {
        // We found the specific plan from the backend
        setFormData((prevData) => ({
          ...prevData,
          seatingPlanID: matchedPlan.seating_id,
        }));
        // We'll NOT override seatingData here if the backend gave us a seatmap
        // unless that seatmap was empty, see below:
        return;
      }
    }

    // If seatPlanID was not provided or not matched, fallback to the first plan
    setFormData((prev) => ({
      ...prev,
      seatingPlanID: filtered[0].seating_id,
    }));

    // Also set the seatingData from that plan if the hall truly has a seatmap
    // (We only do this if we haven't already set a seatmap from the backend.)
    // But for simplicity, let's always set it if the user hasn't set anything yet.
    setSeatingData(JSON.parse(filtered[0]?.seating_json || "{}"));
  };

  /**
   * 3) If editing, fetch the event and populate form
   */
  useEffect(() => {
    if (!id) return; // creating new event, skip

    events_service.getEventsList().then((res) => {
      const eventsList = res.data.status.events;
      if (!eventsList) return;
      const event = eventsList.find((ev: any) => ev.event_id === id);
      if (event) {
        dispatch(setCurrentEvent(event));
        const newFormData = {
          ...formData,
          eventName_en: event.eventName_en || "",
          eventName_am: event.eventName_am || "",
          description_en: event.description_en || "",
          description_am: event.description_am || "",
          eventImage: event.imageurl || null,
          dateTime: event.dateTime || "",
          eventCategory: event.eventCategory || "",
          hallID: event.hallID || "",
          status: event.status || "Draft",
          seatingPlanID: event.seatingPlanID || "",
          seat_count: event.seatCount || 0,
          price_seat: event.seatPrice || 0,
          closedSeatsCount: event.closedSeatsCount || 0,
          invitationSeatsCount: event.invitationSeatsCount || 0,
          hasSeatmap:
            event.hassetamap && /^true$/i.test(event.hassetamap) ? true : false,
        };

        setFormData(newFormData);
        setImagePreview(event.imageurl || null);

        // Set seat counts & comments
        setCommentOfClosedSeats(event.commentOfClosedSeats || "");
        setCommentOfInvitationSeats(event.commentOfInvitationSeats || "");
        setCountOfClosedSeats(event.closedSeatsCount || 0);
        setCountOfInvitationSeats(event.invitationSeatsCount || 0);
        // Determine if selected hall has a seatmap
        const selectedHall = hallsList.find(
          (hall: any) => hall.hall_id === event.hallID
        );
        if (selectedHall) {
          const isHallMapped = /^true$/i.test(selectedHall.hasSeatmap);
          setSelectedHallHasSeatmap(isHallMapped);
          newFormData.hasSeatmap = isHallMapped;
        }

        // If we have a seatmap from the backend (event.seatmap) and it's not empty,
        // set that as the seating data. If it's empty, we'll fallback to the plan's seatmap.
        let backendSeatmap = {};
        if (event.seatmap) {
          try {
            const parsed = JSON.parse(event.seatmap);
            if (Object.keys(parsed).length > 0) {
              backendSeatmap = parsed; // non-empty
            }
          } catch (err) {
            // if parse fails, treat seatmap as empty
          }
        }

        // If hall has a seatmap:
        // 1) We'll first filter seating plans for that hall
        // 2) If the event.seatmap is non-empty, we use it
        //    Otherwise, we fallback to the seat plan's seatmap
        if (newFormData.hasSeatmap) {
          filterSeatingPlans(newFormData.hallID, newFormData.seatingPlanID);
          if (Object.keys(backendSeatmap).length > 0) {
            setSeatingData(backendSeatmap);
          } else {
            // seatmap from backend was empty, so we rely on the "first plan" logic
            // in filterSeatingPlans to set the seatmap from the plan.
          }
        } else {
          // If there's no seatmap in the hall, we do nothing special
          setSeatingData({});
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, hallsList]);

  /**
   * handleChange for all inputs (including file)
   */
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked, files } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox" ? checked : type === "file" ? files![0] : value,
    }));

    // If user changes hall -> re-check seatmap and filter seating plans
    if (name === "hallID") {
      const selectedHall = hallsList.find(
        (hall: any) => hall.hall_id === value
      );
      if (selectedHall) {
        const isHallMapped = /^true$/i.test(selectedHall.hasSeatmap);
        setSelectedHallHasSeatmap(isHallMapped);

        setFormData((prevData) => ({
          ...prevData,
          hasSeatmap: isHallMapped,
        }));
      }
      filterSeatingPlans(value); // fallback to first plan if any
    }

    // If user uploads a file -> generate preview
    if (type === "file" && files && files[0]) {
      const file = files[0];
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  /**
   * Handle seating plan dropdown selection
   */
  const handleSeatingPlanChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = event.target;
    setFormData((prev) => ({
      ...prev,
      seatingPlanID: value,
    }));

    const selectedPlan = filteredSeatingPlans.find(
      (plan) => plan.seating_id === value
    );
    if (selectedPlan) {
      setSeatingData(JSON.parse(selectedPlan.seating_json || "{}"));
    }
  };

  /**
   * Callback from <EventSeatingPlanTable/>
   */
  const handleSeatingDataChange = (updatedSeatingData: {
    [key: string]: { [key: string]: SeatingCell };
  }) => {
    setSeatingData(updatedSeatingData);
  };

  /**
   * Submit / Save
   */
  const handleSubmit = () => {
    setLoading(true);

    // Validate required fields
    const newErrors = {
      eventName_en: !formData.eventName_en.trim(),
      eventName_am: !formData.eventName_am.trim(),
      dateTime: !formData.dateTime,
      eventCategory: !formData.eventCategory,
      hallID: !formData.hallID,
    };
    setErrors(newErrors);

    if (Object.values(newErrors).some((err) => err)) {
      setLoading(false);
      return;
    }

    // Build FormData for backend
    const fd = new FormData();
    fd.append("eventName_en", formData.eventName_en);
    fd.append("eventName_am", formData.eventName_am);
    fd.append("description_en", formData.description_en);
    fd.append("description_am", formData.description_am);
    fd.append("dateTime", formData.dateTime);
    fd.append("eventCategory", formData.eventCategory);
    fd.append("hallID", formData.hallID);
    fd.append("seatingPlanID", formData.seatingPlanID);
    fd.append("status", formData.status);
    fd.append("hasSeatmap", formData.hasSeatmap ? "true" : "false");

    // If seatmap is used
    if (formData.hasSeatmap) {
      fd.append("seatmap", JSON.stringify(seatingData || {}));
    } else {
      // No seatmap
      fd.append("seat_count", String(formData.seat_count || 0));
      fd.append("price_seat", String(formData.price_seat || 0));
      fd.append("closedSeatsCount", String(countOfClosedSeats ?? 0));
      fd.append("invitationSeatsCount", String(countOfInvitationSeats ?? 0));
      fd.append("commentOfClosedSeats", commentOfClosedSeats || "");
      fd.append("commentOfInvitationSeats", commentOfInvitationSeats || "");
    }

    // Image file or existing image URL
    if (formData.eventImage && typeof formData.eventImage === "object") {
      fd.append("eventImage", formData.eventImage);
    } else if (typeof formData.eventImage === "string") {
      fd.append("existingImageUrl", formData.eventImage);
    }

    // If editing
    if (id) {
      fd.append("event_id", id);
    }

    // Create or Update
    const requestPromise = id
      ? events_service.updateEvent(fd)
      : events_service.createEvent(fd);

    requestPromise
      .then((res) => {
        setLoading(false);
        if (res.status === 200) {
          toast.success(
            id ? t("Event updated successfully") : t("Event created successfully")
          );
          setTimeout(() => {
            navigate("/events");
          }, 1500);
        } else {
          toast.error(id ? t("Failed to update event") : t("Failed to create event"));
        }
      })
      .catch(() => {
        setLoading(false);
        toast.error(id ? t("Failed to update event") : t("Failed to create event"));
      });
  };

  return (
    <StyledRoot>
      <Backdrop 
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          color: "#fff"
        }} 
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <StyledContainer>
        {/* Header Bar: Edit or Add */}
        <ButtonContainer>
          <Typography variant="body1">
            {id ? t("Edit Event") : t("Add New Event")}
          </Typography>
          <Box>
            {id && (
              <ReportButton
                variant="contained"
                size="medium"
                style={{ marginRight: "15px" }}
                onClick={() => navigate(`/events/details/${id}`)}
              >
                {t("Report")}
              </ReportButton>
            )}
            <AddEventButton handleSubmit={handleSubmit} />
          </Box>
        </ButtonContainer>

        {/* Event Summary (for edit mode only) */}
        {id && (
          <Box 
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: 2,
              backgroundColor: "#f5f5f5",
              borderRadius: 1,
              marginBottom: 2,
              cursor: "pointer",
              "&:hover": {
                backgroundColor: "#eeeeee",
              },
            }}
            onClick={() => setShowEventDetails(!showEventDetails)}
          >
            <Box sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
            }}>
              <Typography variant="h6" style={{ fontWeight: "bold" }}>
                {formData.eventName_en || formData.eventName_am || t("Untitled Event")}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {formData.dateTime ? new Date(formData.dateTime).toLocaleDateString() : t("No date set")}
              </Typography>
            </Box>
            <IconButton sx={{ color: "#4D67EB" }}>
              {showEventDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        )}

        {/* Collapsible Event Details */}
        <Collapse in={showEventDetails}>
          <Box sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}>
          <Grid container spacing={2}>
            {/* Event Image */}
            <Grid item xs={12} sm={6}>
              <Typography variant="body1">{t("Event Image")}</Typography>
              <TextField
                type="file"
                variant="outlined"
                sx={{ width: "100%", fontSize: "12px" }}
                name="eventImage"
                onChange={handleChange}
                size="small"
              />
              {imagePreview && (
                <img
                  src={imagePreview}
                  alt={t("Event")}
                  style={{
                    width: "100%",
                    maxHeight: "150px",
                    marginTop: "16px",
                    objectFit: "contain",
                  }}
                />
              )}
            </Grid>

            {/* Date & Time */}
            <Grid item xs={12} sm={6}>
              <Typography variant="body1">{t("Date & Time")}</Typography>
              <TextField
                type="datetime-local"
                variant="outlined"
                sx={{ width: "100%", fontSize: "12px" }}
                name="dateTime"
                value={formData.dateTime}
                onChange={handleChange}
                required
                error={errors.dateTime}
                helperText={errors.dateTime && t("Date & Time is required")}
                size="small"
              />
            </Grid>

            {/* Event Category */}
            <Grid item xs={12} sm={6}>
              <Typography variant="body1">{t("Event Category")}</Typography>
              <TextField
                select
                variant="outlined"
                sx={{ width: "100%" }}
                name="eventCategory"
                value={formData.eventCategory}
                onChange={handleChange}
                required
                error={errors.eventCategory}
                helperText={
                  errors.eventCategory && t("Event Category is required")
                }
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: { maxHeight: 200, overflowY: 'auto' },
                    },
                  },
                }}
                size="small"
              >
                <MenuItem value="Classical Music">{t("Classical Music")}</MenuItem>
                <MenuItem value="Orchestral Music">{t("Orchestral Music")}</MenuItem>
                <MenuItem value="Chamber Music">{t("Chamber Music")}</MenuItem>
                <MenuItem value="Traditional / Folk Music">
                  {t("Traditional / Folk Music")}
                </MenuItem>
                <MenuItem value="Jazz & Contemporary">
                  {t("Jazz & Contemporary")}
                </MenuItem>
                <MenuItem value="Rock, Alternative">{t("Rock, Alternative")}</MenuItem>
                <MenuItem value="Electronic Music">{t("Electronic Music")}</MenuItem>
                <MenuItem value="Film & Popular Music">
                  {t("Film & Popular Music")}
                </MenuItem>
                <MenuItem value="Special & Themed Concerts">
                  {t("Special & Themed Concerts")}
                </MenuItem>
                <MenuItem value="Masterclasses & Workshops">
                  {t("Masterclasses & Workshops")}
                </MenuItem>
                <MenuItem value="Youth & Family Concerts">
                  {t("Youth & Family Concerts")}
                </MenuItem>
                <MenuItem value="Educational Talks & Lectures">
                  {t("Educational Talks & Lectures")}
                </MenuItem>
                <MenuItem value="Exhibitions">{t("Exhibitions")}</MenuItem>
                <MenuItem value="Other">{t("Other")}</MenuItem>
              </TextField>
            </Grid>

            {/* Status */}
            <Grid item xs={12} sm={6}>
              <Typography variant="body1">{t("Status")}</Typography>
              <TextField
                select
                variant="outlined"
                sx={{ width: "100%" }}
                name="status"
                value={formData.status}
                onChange={handleChange}
                required
                size="small"
              >
                <MenuItem value="Draft">{t("Draft")}</MenuItem>
                <MenuItem value="Publish">{t("Publish")}</MenuItem>
              </TextField>
            </Grid>

            {/* Hall Selection */}
            <Grid item xs={12} sm={6}>
              <Typography variant="body1">{t("Hall")}</Typography>
              <TextField
                select
                variant="outlined"
                sx={{ width: "100%" }}
                name="hallID"
                value={formData.hallID}
                onChange={handleChange}
                required
                error={errors.hallID}
                helperText={errors.hallID && t("Hall is required")}
                size="small"
              >
                {hallsList.map((hall: any) => (
                  <MenuItem key={hall.hall_id} value={hall.hall_id}>
                    {language === "en" ? hall.hallName_en : hall.hallName_am}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* If NO seatmap => seat_count & price_seat */}
            {!selectedHallHasSeatmap && (
              <>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1">{t("Seat Count")}</Typography>
                  <TextField
                    type="number"
                    variant="outlined"
                    sx={{ width: "100%", fontSize: "12px" }}
                    placeholder={t("Enter seat count")}
                    name="seat_count"
                    value={formData.seat_count ?? ""}
                    onChange={handleChange}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1">{t("Price (per seat)")}</Typography>
                  <TextField
                    type="number"
                    variant="outlined"
                    sx={{ width: "100%", fontSize: "12px" }}
                    placeholder={t("Enter seat price")}
                    name="price_seat"
                    value={formData.price_seat ?? ""}
                    onChange={handleChange}
                    size="small"
                  />
                </Grid>
              </>
            )}

            {/* If hall HAS seatmap => show seating plan dropdown */}
            {selectedHallHasSeatmap && filteredSeatingPlans.length > 0 && (
              <Grid item xs={12} sm={6}>
                <Typography variant="body1">{t("Seating Plan")}</Typography>
                <TextField
                  select
                  variant="outlined"
                  sx={{ width: "100%" }}
                  name="seatingPlanID"
                  value={formData.seatingPlanID}
                  onChange={handleSeatingPlanChange}
                  required
                  size="small"
                >
                  {filteredSeatingPlans.map((plan: any) => (
                    <MenuItem key={plan.seating_id} value={plan.seating_id}>
                      {plan.seating_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}

            {/* Language Tabs */}
            <Grid item xs={12}>
              <Box sx={{
                display: "flex",
                alignItems: "center",
              }}>
                <Tabs
                  value={language}
                  onChange={(e, newValue) => setLanguage(newValue)}
                  aria-label="Language Tabs"
                >
                  <Tab
                    icon={
                      <ReactCountryFlag
                        countryCode="US"
                        svg
                        style={{ marginRight: 10 }}
                      />
                    }
                    value="en"
                    label={t("English")}
                  />
                  <Tab
                    icon={
                      <ReactCountryFlag
                        countryCode="AM"
                        svg
                        style={{ marginRight: 10 }}
                      />
                    }
                    value="am"
                    label={t("Armenian")}
                  />
                </Tabs>
              </Box>
            </Grid>

            {/* Show error if required name fields are missing */}
            <Grid item xs={12}>
              {(errors.eventName_en || errors.eventName_am) && (
                <Typography sx={{
                  color: "#fff",
                  backgroundColor: "rgba(255, 0, 0, 0.4)",
                  padding: 1,
                  borderRadius: 1,
                  marginTop: 1,
                }}>
                  {t("Please fill out the event name in both languages.")}
                </Typography>
              )}
            </Grid>

            {/* Event Name (dynamic by language) */}
            <Grid item xs={12} sm={6}>
              <Typography variant="body1">
                {language === "en"
                  ? t("Event Name (English)")
                  : t("Event Name (Armenian)")}
              </Typography>
              <TextField
                variant="outlined"
                sx={{ width: "100%", fontSize: "12px" }}
                name={`eventName_${language}`}
                value={formData[`eventName_${language}`]}
                onChange={handleChange}
                required
                error={errors[`eventName_${language}`]}
                helperText={
                  errors[`eventName_${language}`]
                    ? t("Event Name is required")
                    : ""
                }
                size="small"
                placeholder={
                  language === "en"
                    ? t("Enter event name in English")
                    : t("Enter event name in Armenian")
                }
              />
            </Grid>

            {/* Event Description (dynamic by language) */}
            <Grid item xs={12} sm={6}>
              <Typography variant="body1">
                {language === "en"
                  ? t("Description (English)")
                  : t("Description (Armenian)")}
              </Typography>
              <TextField
                variant="outlined"
                sx={{ width: "100%", fontSize: "12px" }}
                name={`description_${language}`}
                value={formData[`description_${language}`]}
                onChange={handleChange}
                multiline
                rows={4}
                size="small"
                placeholder={
                  language === "en"
                    ? t("Enter event description in English")
                    : t("Enter event description in Armenian")
                }
              />
            </Grid>

          </Grid>
        </Box>
        </Collapse>

        {/* Seating Table (always visible) */}
        <Box style={{ marginTop: "20px" }}>
          <EventSeatingPlanTable
            key={`${formData.hallID}-${formData.seatingPlanID}`}
            seatingData={seatingData}
            onSeatingDataChange={handleSeatingDataChange}
            selectedHallHasSeatmap={selectedHallHasSeatmap}
            countOfClosedSeats={countOfClosedSeats}
            countOfInvitationSeats={countOfInvitationSeats}
            commentOfClosedSeats={commentOfClosedSeats}
            commentOfInvitationSeats={commentOfInvitationSeats}
            setCountOfClosedSeats={setCountOfClosedSeats}
            setCommentOfClosedSeats={setCommentOfClosedSeats}
            setCountOfInvitationSeats={setCountOfInvitationSeats}
            setCommentOfInvitationSeats={setCommentOfInvitationSeats}
            eventName_en={formData.eventName_en}
            eventName_am={formData.eventName_am}
            eventDateTime={formData.dateTime}
            eventCategory={formData.eventCategory}
            hallName_en={hallsList.find(h => h.hall_id === formData.hallID)?.hallName_en}
            hallName_am={hallsList.find(h => h.hall_id === formData.hallID)?.hallName_am}
            currentLanguage={language}
          />
        </Box>
      </StyledContainer>
    </StyledRoot>
  );
};

/** "Save" Button */
const AddEventButton: React.FC<{ handleSubmit: () => void }> = ({
  handleSubmit,
}) => {
  const { t } = useTranslation();
  return (
    <SaveButton
      variant="contained"
      size="large"
      onClick={handleSubmit}
    >
      {t("Save")}
    </SaveButton>
  );
};

export default EventsCreationPage;
